import { doc, setDoc, Timestamp, getDoc } from 'firebase/firestore';
import { FirebaseStorage, FirestoreDB, LogAnalyticsEvent } from '../../../utils/firebase/firebase';
import { ref } from 'firebase/storage';
import { UploadFileToStorage } from '../../../utils/firebase/helper';
import { v4 as uuidv4 } from 'uuid';
import { ANALYTICS_KEYS } from '../../../constants/AnalyticKeys';
import { saveDoctorNotes, updatePrivateFlagOnly } from '../videoUploadApi';
import { UpdatedDoctorFeedbackNotesToVault } from '../../../api/feedback';
import { showToastMessage } from '../videoUploadUtils';
import { ACM } from '../../../constants/UserRoles';
import { updateGoalDataCollection } from '../../../api/students';

/**
 * Updates goal sharing when a video is uploaded with selected goals
 * This ensures that goals are shared with students and teachers as specified
 */
const updateGoalSharingForVideo = async (
  selectedGoalIds,
  editAndViewIds,
  viewOnlyIds,
  studentId,
  user,
  typeOfUser,
  preRegisteredUsers = []
) => {
  if (!selectedGoalIds || selectedGoalIds.length === 0) {
    return; // No goals to update
  }

  if (!editAndViewIds || editAndViewIds.length === 0) {
    if (!viewOnlyIds || viewOnlyIds.length === 0) {
      return; // No sharing specified
    }
  }

  // Determine collection name
  const isPreRegistered = preRegisteredUsers.some(
    (preUser) => preUser.id === studentId
  );
  const collectionName = isPreRegistered
    ? 'preRegisteredUsers'
    : ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isPatient
    ? 'patientsList'
    : 'studentsList';

  // Prepare sharing arrays
  const editAccess = (editAndViewIds || []).map((id) => ({
    uid: id,
    approved: false,
  }));

  const viewOnlyAccess = (viewOnlyIds || []).map((id) => ({
    uid: id,
    approved: false,
  }));

  // Update each selected goal
  for (const goalId of selectedGoalIds) {
    try {
      // Get the goal document
      const goalRef = doc(
        FirestoreDB,
        collectionName,
        studentId,
        'dataCollection',
        goalId
      );
      const goalDoc = await getDoc(goalRef);

      if (!goalDoc.exists()) {
        console.warn(`Goal ${goalId} not found for student ${studentId}`);
        continue;
      }

      const goalData = goalDoc.data();
      let goalInfo;

      // Decode the goal info
      try {
        if (goalData.isEncrypted) {
          // If encrypted, we can't decode it here - skip for now
          // The cloud function will handle encrypted goals
          console.warn(`Goal ${goalId} is encrypted, skipping client-side update`);
          continue;
        } else {
          const decodedStr = atob(goalData.info);
          goalInfo = JSON.parse(decodedStr);
        }
      } catch (error) {
        console.error(`Error decoding goal ${goalId}:`, error);
        continue;
      }

      // Update ShareWithTeam
      const existingEditAccess = goalInfo.ShareWithTeam?.EditAccess || [];
      const existingViewOnlyAccess = goalInfo.ShareWithTeam?.ViewOnlyAccess || [];

      // Get the goal's student ID to ensure we're sharing with the right student
      const goalStudentId = goalInfo.GoalDetails?.StudentID || goalInfo.StudentID;

      // Merge existing access with new access, avoiding duplicates
      const mergedEditAccess = [...existingEditAccess];
      editAccess.forEach((newAccess) => {
        const existingIndex = mergedEditAccess.findIndex(
          (a) => a.uid === newAccess.uid
        );
        if (existingIndex >= 0) {
          // Keep existing entry (may have approved status)
          // Don't overwrite if already exists
        } else {
          mergedEditAccess.push(newAccess);
        }
      });

      const mergedViewOnlyAccess = [...existingViewOnlyAccess];
      viewOnlyAccess.forEach((newAccess) => {
        const existingIndex = mergedViewOnlyAccess.findIndex(
          (a) => a.uid === newAccess.uid
        );
        const existsInEdit = mergedEditAccess.some((a) => a.uid === newAccess.uid);
        if (existingIndex >= 0) {
          // Keep existing entry
        } else if (!existsInEdit) {
          // Only add if not already in edit access
          mergedViewOnlyAccess.push(newAccess);
        }
      });

      // Ensure the student can see goals that belong to them
      // If the goal belongs to this student, ensure they have approved access
      // This is critical because the filtering logic requires approved: true for ShareWithTeam
      if (goalStudentId === studentId) {
        // Check if student is in editAndViewIds or viewOnlyIds
        const studentInEditAccess = editAndViewIds && editAndViewIds.includes(studentId);
        const studentInViewOnly = viewOnlyIds && viewOnlyIds.includes(studentId);
        
        // Always ensure student has approved access to their own goals
        // This ensures they can see goals shared via video upload
        if (studentInEditAccess) {
          // Add or update student in edit access with approved: true
          const studentIndex = mergedEditAccess.findIndex((a) => a.uid === studentId);
          if (studentIndex >= 0) {
            // Update existing entry to ensure approved: true
            mergedEditAccess[studentIndex] = { ...mergedEditAccess[studentIndex], approved: true };
          } else {
            mergedEditAccess.push({ uid: studentId, approved: true });
          }
        } else if (studentInViewOnly) {
          // Add or update student in view-only access with approved: true
          const studentExistsInEdit = mergedEditAccess.some((a) => a.uid === studentId);
          if (!studentExistsInEdit) {
            const studentIndex = mergedViewOnlyAccess.findIndex((a) => a.uid === studentId);
            if (studentIndex >= 0) {
              // Update existing entry to ensure approved: true
              mergedViewOnlyAccess[studentIndex] = { ...mergedViewOnlyAccess[studentIndex], approved: true };
            } else {
              mergedViewOnlyAccess.push({ uid: studentId, approved: true });
            }
          }
        } else {
          // Even if student is not explicitly in sharing list, ensure they have access to their own goal
          // Add them to view-only access with approved: true if not already in edit access
          const studentExistsInEdit = mergedEditAccess.some((a) => a.uid === studentId);
          const studentExistsInView = mergedViewOnlyAccess.some((a) => a.uid === studentId);
          if (!studentExistsInEdit && !studentExistsInView) {
            mergedViewOnlyAccess.push({ uid: studentId, approved: true });
          } else if (studentExistsInEdit) {
            // Ensure approved: true
            const studentIndex = mergedEditAccess.findIndex((a) => a.uid === studentId);
            if (studentIndex >= 0 && !mergedEditAccess[studentIndex].approved) {
              mergedEditAccess[studentIndex] = { ...mergedEditAccess[studentIndex], approved: true };
            }
          } else if (studentExistsInView) {
            // Ensure approved: true
            const studentIndex = mergedViewOnlyAccess.findIndex((a) => a.uid === studentId);
            if (studentIndex >= 0 && !mergedViewOnlyAccess[studentIndex].approved) {
              mergedViewOnlyAccess[studentIndex] = { ...mergedViewOnlyAccess[studentIndex], approved: true };
            }
          }
        }
      }

      // Update goal info
      goalInfo.ShareWithTeam = {
        IsSharingEnabled:
          mergedEditAccess.length > 0 || mergedViewOnlyAccess.length > 0,
        EditAccess: mergedEditAccess,
        ViewOnlyAccess: mergedViewOnlyAccess,
      };

      // Save updated goal
      const encodedInfo = btoa(JSON.stringify(goalInfo));
      await updateGoalDataCollection(
        user?.uid,
        typeOfUser,
        studentId,
        goalId,
        encodedInfo
      );

    } catch (error) {
      console.error(`Error updating goal ${goalId} sharing:`, error);
      // Continue with other goals even if one fails
    }
  }
};

const useUploadVideoProcessing = () => {
  const processVideoUpload = async (
    uploadedFile,
    customFileName,
    fileDuration,
    date,
    user,
    typeOfUser,
    selectedStudent,
    selectedPatient,
    therapyType,
    interventions,
    antecedent,
    consequence,
    selectCategory,
    goalAssociatedWithBehavior,
    selectedGoalIds,
    selectedBehaviorIds,
    selectedSkillIds,
    otherInputValue,
    editAndViewIds,
    viewOnlyIds,
    information,
    videoFeedBackNotesPrivate,
    setIsLoading,
    setError,
    addVideoNotification,
    patientData,
    students,
    teacherData,
    queryClient,
    handleClose,
    preRegisteredUsers = []
  ) => {
    try {
      setIsLoading(true);
      setError({ show: false, message: '' });

      const id =
        ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher
          ? ACM[typeOfUser]?.isDoctor
            ? selectedPatient
            : selectedStudent
          : user?.uid;

      if (uploadedFile && uploadedFile.type === 'video/mp4') {
        const videoID = uuidv4();
        const blob = new Blob([uploadedFile], { type: 'video/mp4' });
        const fileName = customFileName
          ? customFileName.charAt(0).toUpperCase() +
            customFileName.slice(1) +
            '.mp4'
          : uploadedFile.name;

        const videoStorageRef = ref(
          FirebaseStorage,
          `videoLogs/${id}/${videoID}/rawVideo/${fileName}`
        );

        const result = await UploadFileToStorage(videoStorageRef, blob);

        if (result.status) {
          const url = result.data;
          const params = {
            duration: fileDuration || 0,
            fileName: fileName,
            fileSize: uploadedFile.size,
            fileType: uploadedFile.type,
            time: Timestamp.now(),
            url: url,
            id: videoID,
            date: date || null,
            userName: user?.fullname,
            userType: typeOfUser,
            therapyType: therapyType,
            interventions: interventions,
            antecedent: antecedent,
            consequence: consequence,
            category: selectCategory,
            goalAssociatedWithBehavior: goalAssociatedWithBehavior,
            selectedGoalIds: selectedGoalIds,
            selectedBehaviorIds: selectedBehaviorIds,
            selectedSkillIds: selectedSkillIds,
            otherValue: otherInputValue,
            videoUploader: user?.uid,
            typeOfUserUploadBy: typeOfUser,
            editAndViewIds: editAndViewIds.map((id) => ({
              id,
              approved: false,
            })),
            viewOnlyIds: viewOnlyIds.map((id) => ({ id, approved: false })),
          };

          await setDoc(doc(FirestoreDB, 'videoLogs', id), { videos: [] });
          await setDoc(
            doc(FirestoreDB, 'videoLogs', id, 'rawVideos', videoID),
            {
              info: btoa(JSON.stringify(params)),
              isEncrypted: false,
            }
          );

          const isStudentOrPatient =
            ACM[typeOfUser]?.isStudent || ACM[typeOfUser]?.isPatient;

          await setDoc(
            doc(FirestoreDB, 'videoLogs', id, 'patientNotes', videoID),
            {
              notes: btoa(isStudentOrPatient ? information?.notes || '' : ''),
              isEncrypted: false,
              isDeleted: false,
            }
          );

          // Handle doctor/teacher notes and private flag
          if (!isStudentOrPatient) {
            try {
              // For teachers and doctors, save to docNotes
              // First create the document with Private flag
              await updatePrivateFlagOnly(
                FirestoreDB,
                selectedStudent,
                selectedPatient,
                videoFeedBackNotesPrivate,
                videoID
              );

              // Then save notes via cloud function
              await saveDoctorNotes(
                UpdatedDoctorFeedbackNotesToVault,
                information?.notes || '',
                user,
                selectedStudent,
                selectedPatient,
                videoID
              );

              // Then update Private flag again
              await updatePrivateFlagOnly(
                FirestoreDB,
                selectedStudent,
                selectedPatient,
                videoFeedBackNotesPrivate,
                videoID
              );
            } catch (error) {
              console.error('Error in doctor/teacher notes handling:', error);
              throw error;
            }
          }

          const videoUploadKey = ACM[typeOfUser]?.isDoctor
            ? ANALYTICS_KEYS.DASHBOARD.DOCTORVIDEOUPLOAD
            : ANALYTICS_KEYS.DASHBOARD.PATIENTVIDEOUPLOAD;

          LogAnalyticsEvent(videoUploadKey, {
            videoID: videoID,
            message: `${typeOfUser?.toLowerCase()} successfully uploaded a video`,
          });

          // Add video upload notifications
          await addVideoNotification(videoID, id, user, typeOfUser, patientData, students, teacherData);

          // Update goal sharing if goals are associated with the video
          if (goalAssociatedWithBehavior && selectedGoalIds && selectedGoalIds.length > 0) {
            try {
              await updateGoalSharingForVideo(
                selectedGoalIds,
                editAndViewIds,
                viewOnlyIds,
                id,
                user,
                typeOfUser,
                preRegisteredUsers
              );
            } catch (error) {
              console.error('Error updating goal sharing:', error);
              // Don't fail the upload if goal sharing update fails
            }
          }

          showToastMessage('success');

          if (ACM[typeOfUser]?.isDoctor || ACM[typeOfUser]?.isTeacher) {
            queryClient.invalidateQueries({
              queryKey: ['allPatientsVideos', user?.uid],
              active: true,
            });
          } else {
            queryClient.invalidateQueries({
              queryKey: ['onePatientsVideos', user?.uid],
              active: true,
            });
          }

          handleClose();
        } else {
          showToastMessage('error', 'Error uploading video. Please try again.');
        }
      } else {
        showToastMessage('error', 'Please upload only mp4 videos!');
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error message: ', error);
      setIsLoading(false);
      showToastMessage('error');
    }
  };

  return {
    processVideoUpload,
  };
};

export default useUploadVideoProcessing;
